import { CustomFabricImage } from '../../../global-types/canvas';
import { addPhotozoneImageMoveConstraint } from './add-photozone-image-move-constraint';
import { doesImageFullyCoverPhotozone, getMinScaleForCoverage } from './apply-image-force-fill-contraints';
import { calculateFabricCoordsCenter } from './calculate-fabric-coords-center';
import { shrinkPt } from './shrink-pt';

/**
 * Ensures a rotated photo still force-fills its photozone.
 *
 * After rotation the image bounding box may no longer cover the photozone.
 * This utility:
 *  1. Refreshes coordinates on both objects.
 *  2. Checks coverage (with a 5 px margin via shrinkPt).
 *  3. If coverage is lost, centers the image on the photozone, then re-checks.
 *  4. If centering alone isn't enough, scales the image up by the minimum
 *     multiplier returned by getMinScaleForCoverage.
 *  5. Rebases the zoom model (scaleXOriginal / scaleYOriginal / scaleValue)
 *     so that 100 % always means "just covers the photozone".
 *  6. Removes any stale `moving` event handler and re-applies the photozone
 *     movement constraint.
 *
 * @param image  – The photo that was just rotated.
 * @param photozoneObject – The photozone the image must cover.
 */
export const applyRotationForceFill = (image: CustomFabricImage, photozoneObject: fabric.Object): void => {
  // 1. Refresh coordinates after rotation
  image.setCoords();
  photozoneObject.setCoords();

  const imageCoords = image.getCoords();
  const photoZoneCoords = photozoneObject.getCoords();
  const center = calculateFabricCoordsCenter(
    photoZoneCoords as [fabric.Point, fabric.Point, fabric.Point, fabric.Point],
  );
  const photoZoneMarginCoords = (photoZoneCoords as Array<{ x: number; y: number }>).map((coord) =>
    shrinkPt({ center, pt: coord }),
  );

  // 2. Check whether the image still covers the photozone
  if (!doesImageFullyCoverPhotozone(imageCoords, photoZoneMarginCoords)) {
    // 3. Center the image on the photozone
    const photoZoneCenter = photozoneObject.getCenterPoint();
    if (photoZoneCenter) {
      image.setPositionByOrigin(photoZoneCenter, 'center', 'center');
      image.setCoords();
    }

    // 4. Re-check — centering alone may restore full coverage
    const centeredImageCoords = image.getCoords();
    if (!doesImageFullyCoverPhotozone(centeredImageCoords, photoZoneMarginCoords)) {
      const minScale = getMinScaleForCoverage(image as fabric.Image, photoZoneMarginCoords);
      if (minScale > 1) {
        image.set({
          scaleX: (image.scaleX ?? 1) * minScale,
          scaleY: (image.scaleY ?? 1) * minScale,
        });
        image.setCoords();
      }
    }

    // 5. Rebase the zoom model so 100 % = current (rotated-coverage) size
    image.data.scaleXOriginal = image.scaleX;
    image.data.scaleYOriginal = image.scaleY;
    image.data.scaleValue = 100;
  }

  // 6. Rebind the movement constraint
  image.off('moving');
  addPhotozoneImageMoveConstraint(image as fabric.Image, photozoneObject).onCallback();
};
